import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown, X } from 'lucide-react';

import { cn } from './cn.js';

/*
 * Поле выбора с поиском: пользователь печатает, вызывающий по тексту запрашивает
 * варианты (обычно у API) и отдаёт их сюда. Нативный <select> этого не умеет, а
 * библиотеку ради одного поля не берём - паттерн combobox из WAI-ARIA 1.2:
 * поле с role="combobox", список role="listbox", активный вариант через
 * aria-activedescendant, фокус остаётся в поле.
 *
 * Пока поле не в фокусе, в нём подпись выбранного значения; начали печатать -
 * это текст поиска. Ушли из поля, не выбрав, - подпись возвращается.
 */

export interface ComboboxProps<T> {
  id?: string;
  value: T | null;
  onChange: (value: T | null) => void;
  /** Вызывается на каждый набранный символ; дебаунс - забота вызывающего. */
  onQueryChange: (query: string) => void;
  options: ReadonlyArray<T>;
  getKey: (option: T) => string;
  getLabel: (option: T) => string;
  /** Разметка строки списка; по умолчанию - подпись. */
  renderOption?: (option: T) => ReactNode;
  isLoading?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  'aria-label'?: string;
}

export const Combobox = <T,>({
  id,
  value,
  onChange,
  onQueryChange,
  options,
  getKey,
  getLabel,
  renderOption,
  isLoading = false,
  placeholder,
  emptyLabel = 'Ничего не найдено',
  disabled = false,
  invalid = false,
  className,
  'aria-label': ariaLabel,
}: ComboboxProps<T>): ReactNode => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-list`;

  const [isOpen, setIsOpen] = useState(false);
  const [typed, setTyped] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const text = typed ?? (value === null ? '' : getLabel(value));
  const activeId =
    isOpen && activeIndex >= 0 && activeIndex < options.length
      ? `${listId}-${activeIndex}`
      : undefined;

  const open = () => {
    if (disabled) return;
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setTyped(null);
    setActiveIndex(-1);
  };

  const select = (option: T) => {
    onChange(option);
    close();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      open();
      setActiveIndex((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && isOpen && activeIndex >= 0) {
      event.preventDefault();
      const option = options[activeIndex];
      if (option !== undefined) select(option);
    } else if (event.key === 'Escape' && isOpen) {
      // Не даём Esc закрыть модальное окно, пока открыт список.
      event.stopPropagation();
      close();
    }
  };

  return (
    <div className={cn('relative', className)}>
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={text}
        onFocus={(event) => {
          event.currentTarget.select();
          onQueryChange('');
          open();
        }}
        onBlur={close}
        onChange={(event) => {
          setTyped(event.target.value);
          setActiveIndex(-1);
          onQueryChange(event.target.value);
          open();
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          'h-[34px] w-full rounded-[7px] border border-ink-200 bg-white pr-14 pl-3 text-[13px] text-ink-900',
          'placeholder:text-ink-400 focus-visible:border-brand-green focus-visible:ring-2',
          'focus-visible:ring-brand-green-50 focus-visible:outline-none disabled:bg-ink-50',
          invalid && 'border-st-delayed',
        )}
      />

      <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center gap-0.5 text-ink-400">
        {value !== null && !disabled && (
          <button
            type="button"
            aria-label="Очистить"
            tabIndex={-1}
            // mousedown, а не click: иначе поле успеет потерять фокус и закрыть список.
            onMouseDown={(event) => {
              event.preventDefault();
              onChange(null);
              close();
            }}
            className="pointer-events-auto grid size-5 cursor-pointer place-items-center rounded hover:bg-ink-100 hover:text-ink-700"
          >
            <X aria-hidden className="size-3.5" />
          </button>
        )}
        <ChevronDown aria-hidden className="size-3.5" />
      </div>

      {isOpen && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-ink-200 bg-white py-1 shadow-[0_8px_24px_rgba(15,23,34,0.12)]"
        >
          {isLoading && options.length === 0 && (
            <li className="px-3 py-2 text-[12.5px] text-ink-500">Ищем...</li>
          )}
          {!isLoading && options.length === 0 && (
            <li className="px-3 py-2 text-[12.5px] text-ink-500">
              {emptyLabel}
            </li>
          )}
          {options.map((option, index) => (
            <li
              key={getKey(option)}
              id={`${listId}-${index}`}
              role="option"
              tabIndex={-1}
              aria-selected={index === activeIndex}
              onMouseDown={(event) => {
                event.preventDefault();
                select(option);
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={cn(
                'cursor-pointer px-3 py-1.5 text-[13px] text-ink-800',
                index === activeIndex && 'bg-brand-green-50 text-ink-900',
              )}
            >
              {renderOption ? renderOption(option) : getLabel(option)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
